import { API_BASE_URL } from "@kupikupi/brand";
import { type ResolvedProduct, isProductUrl, resolveProductUrl } from "@kupikupi/api";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";

/**
 * Mini App counterpart of `apps/web/lib/product-url-search.ts` (web #160).
 *
 * The home hero search and the catalog search box let a user *paste a
 * source-platform product link* (Taobao / Tmall / 1688 / Weidian / Pinduoduo)
 * instead of a keyword. Without this, a pasted link was keyword-searched and
 * returned 0 results — the reverse-海淘 "下指定货" entry point was missing on
 * mini (it works on web). This hook restores it.
 *
 * Detection (`isProductUrl`) and backend resolution (`resolveProductUrl`) are
 * shared with web via `@kupikupi/api`, so the supported host list and the
 * channel mapping stay identical across both surfaces — there is exactly one
 * source of truth. The only mini-specific part is the navigation target: a
 * TanStack-Router push to the existing `/product/$channel/$spuNo` route.
 */

/** Pure decision: what should a submitted search box query do? */
export type SearchAction =
  | { kind: "resolve"; url: string }
  | { kind: "keyword"; keyword: string };

/**
 * Pure, framework-free classifier for a *raw* (untrimmed) search submission.
 * Trims once, then routes a supported product URL to backend resolution and
 * everything else (keywords, blank input, non-supported URLs) to keyword
 * search. Unit-tested in isolation; the hook below only wires it to the router.
 */
export function decideSearchAction(rawQuery: string): SearchAction {
  const q = rawQuery.trim();
  if (isProductUrl(q)) return { kind: "resolve", url: q };
  return { kind: "keyword", keyword: q };
}

export interface ProductUrlSearchHandlers {
  /** Non-URL input (or blank) — run the existing keyword search. */
  onKeywordSearch: (keyword: string) => void;
  /** Looked like a product URL but the backend couldn't resolve it to a PDP. */
  onResolveFailed: () => void;
}

export function useProductUrlSearch() {
  const navigate = useNavigate();
  const [isResolving, setIsResolving] = useState(false);

  /**
   * Handle a raw search submission. Returns a promise that settles once the
   * resolve round-trip (if any) completes, so callers can `await` it.
   */
  const submit = async (
    rawQuery: string,
    { onKeywordSearch, onResolveFailed }: ProductUrlSearchHandlers,
  ): Promise<void> => {
    if (isResolving) return;
    const action = decideSearchAction(rawQuery);

    if (action.kind === "keyword") {
      onKeywordSearch(action.keyword);
      return;
    }

    // Pasted source-platform product URL → resolve to a PDP and jump straight
    // there. The backend does the channel+spuNo extraction; we only navigate.
    setIsResolving(true);
    try {
      const resolved: ResolvedProduct | null = await resolveProductUrl(
        { baseUrl: API_BASE_URL },
        action.url,
      );
      if (resolved) {
        navigate({
          to: "/product/$channel/$spuNo" as never,
          params: { channel: resolved.channel, spuNo: resolved.spuNo } as never,
        });
        return;
      }
      onResolveFailed();
    } catch {
      onResolveFailed();
    } finally {
      setIsResolving(false);
    }
  };

  return { submit, isResolving };
}
